import { APP_INITIALIZER, ApplicationConfig, inject } from '@angular/core';
import { provideRouter, withPreloading, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, HTTP_INTERCEPTORS, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { firstValueFrom } from 'rxjs';

import { AuthInterceptor, AuthService } from '@ui-nuvertos/auth';
import { TenantModuleService } from '@ui-nuvertos/shared';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';
import Lara from '@primeuix/themes/aura';

import { appRoutes } from './app.routes';
import { SelectivePreloadingStrategy } from './strategies/selective-preloading.strategy';
import { PerformanceService } from './services/performance.service';

// See apps/shell/src/app/app.config.ts for why this isn't in libs/auth — federation
// sharing libs/auth would route TenantModuleService injects through a different
// bundled libs/shared, producing two service instances and an empty sidebar.
const authSessionInit = {
  provide: APP_INITIALIZER,
  multi: true,
  useFactory: () => {
    const tenantModules = inject(TenantModuleService);
    const auth = inject(AuthService);
    return async () => {
      await tenantModules.init();
      try {
        const user = await firstValueFrom(auth.loadUserSession());
        if (user) {
          await tenantModules.loadAfterLogin();
        }
      } catch {
        // Defensive only.
      }
    };
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    // Register interceptor BEFORE provideHttpClient to ensure it's available
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    // Enable HTTP_INTERCEPTORS token for standalone apps
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Lara,
        options: {
          darkModeSelector: 'none',
        },
      },
    }),
    provideRouter(
      appRoutes,
      withPreloading(SelectivePreloadingStrategy), // Custom selective preloading
      withInMemoryScrolling({
        scrollPositionRestoration: 'top', // Scroll to top on navigation
        anchorScrolling: 'enabled' // Enable anchor scrolling
      })
    ),
    MessageService,
    PerformanceService, // Performance monitoring
    authSessionInit, // Hydrate IDB + restore session + fetch modules if authed
  ]
};